import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router";
import type { AbilitiesEvaluation } from "vue-simple-acl/types/acl";
import { BREADCRUMBS } from "~/core/constants/breadcrumbs";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { canWithRedirection } from "~/core/utils/canWithRedirection";
import {
  PATH_USER_ACCOUNT,
  PATH_USER_ACCOUNT_ADMINISTRATION,
  PATH_USER_ACCOUNT_ADMINISTRATION_LANGUAGE,
  PATH_USER_ACCOUNT_ADMINISTRATION_PROFILE,
  PATH_USER_ACCOUNT_LANGUAGE,
  PATH_USER_ACCOUNT_PROFILE,
  PATH_USER_MANAGEMENT,
  PATH_USER_MANAGEMENT_GROUPS,
  PATH_USER_MANAGEMENT_GROUPS_CODE,
  PATH_USER_MANAGEMENT_PROFILES,
  PATH_USER_MANAGEMENT_PROFILES_CODE,
  PATH_USER_MANAGEMENT_USERS,
  PATH_USER_MANAGEMENT_USERS_LOGIN,
} from "~/modules/user/constants";
import UserAccountPage from "~/modules/user/views/UserAccountPage.vue";
import UserGroupFormPage from "~/modules/user/views/UserGroupFormPage.vue";
import UserManagementPage from "~/modules/user/views/UserManagementPage.vue";

const routes: RouteRecordRaw[] = [
  // User account pages
  {
    // The user management page does not exist by itself, it is a wrapper for the profile and language pages
    // accessible via the usage of tabs. The navigation to this page (via the left panel or the URL) must redirect
    // to the user tab.
    path: PATH_USER_ACCOUNT,
    redirect: PATH_USER_ACCOUNT_PROFILE,
  },
  {
    path: PATH_USER_ACCOUNT_PROFILE,
    component: UserAccountPage,
    meta: {
      breadcrumb: BREADCRUMBS.USER_ACCOUNT,
    },
  },
  {
    path: PATH_USER_ACCOUNT_LANGUAGE,
    component: UserAccountPage,
    meta: {
      breadcrumb: BREADCRUMBS.USER_ACCOUNT,
    },
  },
  {
    path: PATH_USER_ACCOUNT_ADMINISTRATION,
    redirect: PATH_USER_ACCOUNT_ADMINISTRATION_PROFILE,
  },
  {
    path: PATH_USER_ACCOUNT_ADMINISTRATION_PROFILE,
    component: UserAccountPage,
    meta: {
      breadcrumb: BREADCRUMBS.USER_ACCOUNT,
      layout: "AdministrationLayout",
    },
  },
  {
    path: PATH_USER_ACCOUNT_ADMINISTRATION_LANGUAGE,
    component: UserAccountPage,
    meta: {
      breadcrumb: BREADCRUMBS.USER_ACCOUNT,
      layout: "AdministrationLayout",
    },
  },
  // User management pages
  {
    // The user management page does not exist by itself, it is a wrapper for the user, group and profile pages
    // accessible via the usage of tabs. The navigation to this page (via the left panel or the URL) must redirect
    // to the user tab.
    path: PATH_USER_MANAGEMENT,
    redirect: PATH_USER_MANAGEMENT_USERS,
  },
  {
    path: PATH_USER_MANAGEMENT_USERS,
    component: UserManagementPage,
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_READ_USER),
      breadcrumb: BREADCRUMBS.USER_MANAGEMENT,
    },
  },
  {
    path: PATH_USER_MANAGEMENT_USERS_LOGIN,
    component: UserManagementPage,
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_READ_USER),
      breadcrumb: BREADCRUMBS.USER_MANAGEMENT,
    },
  },
  // user group management page
  {
    path: PATH_USER_MANAGEMENT_GROUPS,
    component: UserManagementPage,
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_READ_USER),
      breadcrumb: BREADCRUMBS.GROUP,
    },
  },
  {
    path: PATH_USER_MANAGEMENT_GROUPS_CODE,
    component: UserGroupFormPage,
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_WRITE_USER),
      breadcrumb: BREADCRUMBS.GROUP,
    },
  },
  // User profile management pages
  {
    path: PATH_USER_MANAGEMENT_PROFILES,
    component: UserManagementPage,
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_READ_USER),
      breadcrumb: BREADCRUMBS.PROFILE,
    },
  },
  {
    path: PATH_USER_MANAGEMENT_PROFILES_CODE,
    component: UserManagementPage,
    meta: {
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: AbilitiesEvaluation,
      ) => canWithRedirection(can, ACLRoles.CAN_WRITE_USER),
      breadcrumb: BREADCRUMBS.PROFILE,
    },
  },
];

export default routes;
